import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { ConfigProvider, Drawer, FloatButton, Modal, Spin } from 'antd';
import { DashboardOutlined, PictureOutlined } from '@ant-design/icons';
import api from '../../services/api';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import MachineDailyStatusCards from './MachineDailyStatusCards';

/**
 * MachineDailyStatusButton - botão flutuante do PWA do operador, visível em
 * todas as telas do app. Abre uma gaveta com o balanço diário por máquina,
 * dividido em MÁQUINA e TANQUE (ambos zeram à meia-noite):
 *  - MÁQUINA: saldo = RC adicionado + solvente processado − descargas de
 *    Recarga/Borra − transferências ao tanque, vs capacidade da máquina.
 *  - TANQUE: nível = última leitura do dia − descargas de Solvente/Água/
 *    Amarelo desde a leitura, vs capacidade do tanque.
 *  - Mostra a composição do saldo (auditoria visual) e alerta quando a máquina
 *    fechou o dia anterior com resíduo relevante (o "0" de hoje pode não
 *    refletir a máquina física).
 *
 * O badge do botão mostra quantas máquinas/tanques estão com ocupação elevada
 * (>90%), para o operador perceber sem precisar abrir.
 *
 * Eficiência: 1 endpoint agregado (GET /api/dashboard/machines-daily-balance),
 * polling de 60s + refetch quando o app volta ao foco. Falhas são silenciosas
 * para não incomodar o operador durante o fluxo.
 */
const POLL_INTERVAL = 60_000;

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

const MachineDailyStatusButton = () => {
  const { loggedUser } = useContext(GeneralContext);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);

  // Zeramento de máquina (troca de turno): Admin, Operador-comprador e Líder.
  // Botão + modal ficam no MachineResetControl (compartilhado com o painel).
  const canReset = EUserTypes.canEditToday(loggedUser);

  // Leitura atual do termômetro (independente das máquinas).
  const [temp, setTemp] = useState(null);
  const [tempImgUrl, setTempImgUrl] = useState(null);
  const [tempImgLoading, setTempImgLoading] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const tempImgUrlRef = useRef(null);
  const tempImgIdRef = useRef(null); // id da leitura da imagem em cache

  const fetchBalance = useCallback(async () => {
    try {
      const res = await api.dashboard.getMachinesDailyBalance();
      setMachines(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // Silencioso: o botão é informativo; erros não devem travar o fluxo do operador.
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar balanço das máquinas:', err?.response?.data?.error || err.message);
    }
  }, []);

  const fetchTemp = useCallback(async () => {
    try {
      const res = await api.temperature.latest();
      setTemp(res.data?.data || null);
    } catch (err) {
      // Silencioso: temperatura é informativa e não deve travar o fluxo.
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar temperatura:', err?.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchTemp();
    const interval = setInterval(() => {
      fetchBalance();
      fetchTemp();
    }, POLL_INTERVAL);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchBalance();
        fetchTemp();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchBalance, fetchTemp]);

  // Revoga o object URL da imagem da temperatura ao desmontar.
  useEffect(() => {
    return () => {
      if (tempImgUrlRef.current) URL.revokeObjectURL(tempImgUrlRef.current);
    };
  }, []);

  // Abre o modal da foto; a imagem só é baixada aqui (nunca junto com o drawer)
  // e fica em cache enquanto a leitura for a mesma.
  const openTempPhoto = useCallback(async () => {
    if (!temp?.id) return;
    setPhotoOpen(true);
    if (tempImgUrl && tempImgIdRef.current === temp.id) return; // já baixada desta leitura
    setTempImgLoading(true);
    try {
      const res = await api.temperature.image(temp.id);
      if (tempImgUrlRef.current) URL.revokeObjectURL(tempImgUrlRef.current);
      const url = URL.createObjectURL(res.data);
      tempImgUrlRef.current = url;
      tempImgIdRef.current = temp.id;
      setTempImgUrl(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar imagem da leitura:', err?.response?.data?.error || err.message);
      setTempImgUrl(null);
    } finally {
      setTempImgLoading(false);
    }
  }, [temp, tempImgUrl]);

  if (!machines.length) return null;

  // Máquinas em alerta (>90% da capacidade da máquina OU do tanque) aparecem no badge do botão.
  const alertCount = machines.filter((m) =>
    (m.occupancyPct != null && m.occupancyPct > 90)
    || (m.tankOccupancyPct != null && m.tankOccupancyPct > 90)
  ).length;

  return (
    <>
      {/* Laranja vivo para destacar o botão sobre o fundo branco/azul do app.
          Em alerta (>90%), vira vermelho para chamar ainda mais atenção. */}
      <ConfigProvider theme={{ token: { colorPrimary: alertCount > 0 ? '#c1281a' : '#fa541c' } }}>
        <FloatButton
          icon={<DashboardOutlined />}
          type="primary"
          badge={alertCount > 0 ? { count: alertCount, color: '#1b3663' } : undefined}
          tooltip="Ocupação das máquinas hoje"
          aria-label={`Ver ocupação das máquinas hoje${alertCount > 0 ? ` — ${alertCount} em alerta` : ''}`}
          onClick={() => setOpen(true)}
          style={{ right: 16, bottom: 'calc(24px + env(safe-area-inset-bottom))' }}
        />
      </ConfigProvider>

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 18, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Máquinas — hoje
            </span>
            {/* Temperatura no header do drawer (some quando ainda não há leitura).
                A foto só é baixada quando o operador toca no chip. */}
            {temp && (
              <button
                type="button"
                onClick={openTempPhoto}
                aria-label={`Temperatura atual: ${fmt(temp.value)} graus ${temp.unit || 'C'}, lida em ${new Date(temp.occurred_at).toLocaleString('pt-BR')}${temp.status === 'alerta' ? ', em alerta (fora da faixa)' : ''}. Toque para ver a foto da leitura.`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  lineHeight: 1.2,
                  border: 'none',
                  borderRadius: 14,
                  padding: '4px 10px',
                  color: '#fff',
                  background: temp.status === 'alerta' ? '#c1281a' : '#0d7dd6',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 700 }}>
                  {fmt(temp.value)}°{temp.unit || 'C'}
                  <PictureOutlined aria-hidden="true" />
                </span>
                {/* Data/hora da leitura, logo abaixo do valor */}
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.9 }}>
                  {new Date(temp.occurred_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </button>
            )}
          </div>
        }
        placement="bottom"
        height="85vh"
        open={open}
        onClose={() => setOpen(false)}
        styles={{ body: { padding: 16, overflowY: 'auto' } }}
      >
        {/* Cards por máquina compartilhados com o Dashboard do painel admin. */}
        <MachineDailyStatusCards machines={machines} canReset={canReset} onReload={fetchBalance} />
      </Drawer>

      {/* Foto da leitura do termômetro — baixada somente ao abrir este modal. */}
      <Modal
        title="Foto da leitura do termômetro"
        open={photoOpen}
        onCancel={() => setPhotoOpen(false)}
        footer={null}
        centered
      >
        {temp && (
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
            {fmt(temp.value)}°{temp.unit || 'C'} · {new Date(temp.occurred_at).toLocaleString('pt-BR')}
            {temp.status === 'alerta' && (
              <span style={{ color: '#c1281a', fontWeight: 600 }}> · fora da faixa (alerta)</span>
            )}
          </div>
        )}
        {tempImgLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><Spin /></div>
        ) : tempImgUrl ? (
          <img
            src={tempImgUrl}
            alt={`Imagem da leitura do termômetro de ${temp ? new Date(temp.occurred_at).toLocaleString('pt-BR') : ''}`}
            style={{ width: '100%', borderRadius: 6, background: '#fff', border: '1px solid #eee' }}
          />
        ) : (
          <div style={{ fontSize: 13, color: '#9ca3af', padding: 12 }}>Imagem indisponível para esta leitura.</div>
        )}
      </Modal>
    </>
  );
};

export default MachineDailyStatusButton;
